import React, { Component } from 'react'

import { RouteComponentProps } from 'react-router-dom'
import { ChildProps } from 'react-apollo'
import { InputProps } from './projectDataQuery'

import get from 'lodash/get'
import defaultTo from 'lodash/defaultTo'
import isNil from 'lodash/isNil'
import assignWith from 'lodash/assignWith'
import pick from 'lodash/pick'
import { getProjectId, getProjectNumber } from 'utils'

import PageLayout from 'components/layout/pageLayout'
import Head from 'components/layout/head'
import Button from 'components/ui/button'
import SubNavigation from 'components/layout/subNavigation'
import Input from 'components/ui/input'
import Select from 'components/ui/select'
import TextArea from 'components/ui/textArea'

import { countryOptions } from 'data'

interface ProjectSettingsProps extends ChildProps<InputProps, Response>, RouteComponentProps<any> {
  authenticated: boolean
  projectData: any
}

interface IErrorObject {
  [k: string]: string | null
}

type ProjectSettingsState = {
  errors: IErrorObject | null
  success: boolean
  projectName: string | null
  company: string | null
  date: Date | Date[] | null
  hourlyRate: number | null
  tax: number | null
  contact: string | null
  street: string | null
  zipCode: string | null
  city: string | null
  country: string | null
  description: string | null
}

class ProjectSettings extends Component<ProjectSettingsProps, ProjectSettingsState> {
  constructor(props: ProjectSettingsProps) {
    super(props)
    this.state = {
      errors: null,
      success: false,
      ...this.formFields,
    }
  }

  formFields = {
    projectName: null,
    company: null,
    date: null,
    hourlyRate: null,
    tax: null,
    contact: null,
    street: null,
    zipCode: null,
    city: null,
    country: null,
    description: null,
  }

  transformProject = (project: any) => {
    if (isNil(project)) {
      return {}
    }
    return {
      ...project,
      ...project.projectDetails,
    }
  }

  getValue = (key: string, onlyProps: boolean = false) => {
    const { getProject: projectData } = this.props.projectData
    const stateValue = get(this.state, key)

    if (!isNil(stateValue) && !onlyProps) {
      return stateValue
    }

    return get(this.transformProject(projectData), key)
  }

  getVariables = () => {
    const { getProject: projectData } = this.props.projectData
    const formFields = Object.keys(this.formFields)
    const variables = assignWith(
      this.transformProject(projectData),
      this.state,
      (objValue: any, srcValue: any) => (isNil(srcValue) ? objValue : srcValue),
    )

    // Filter out non form fields from state object
    return pick(variables, formFields)
  }

  isEmpty = (value: string) => isNil(value) || value === ''

  handleSave = () => {
    const { mutate } = this.props
    const id = getProjectId(this.props)

    const errors = {
      projectName: this.isEmpty(this.getValue('projectName')) ? 'Error Message.' : null,
      tax: this.isEmpty(this.getValue('tax')) ? 'Error Message.' : null,
      contact: this.isEmpty(this.getValue('contact')) ? 'Error Message.' : null,
      street: this.isEmpty(this.getValue('street')) ? 'Error Message.' : null,
      zipCode: this.isEmpty(this.getValue('zipCode')) ? 'Error Message.' : null,
      city: this.isEmpty(this.getValue('city')) ? 'Error Message.' : null,
      country: this.isEmpty(this.getValue('country')) ? 'Error Message.' : null,
    }

    const hasErrors = Object.values(errors).find((error) => {
      if (error) return true
      return false
    })

    if (hasErrors) {
      this.setState({ errors, success: false })
    } else {
      if (mutate) {
        mutate({
          variables: { ...this.getVariables(), currentProjectNumber: id, projectNumber: id },
          refetchQueries: ['getProject'],
        })
          .then((res) => {
            // Project settings update was sucessfull
            if (get(res, 'data.updateProject')) {
              this.setState({ errors: null, success: true })
            } else {
              this.setState({
                errors: {
                  api: 'Error Message',
                },
                success: false,
              })
            }
          })
          .catch(() => {
            this.setState({
              errors: {
                api: 'Error Message',
              },
              success: false,
            })
          })
      }
    }
  }

  render() {
    const id = getProjectId(this.props)
    const { errors, success } = this.state

    return (
      <PageLayout pageTitle="Projekte">
        <Head
          headline={get(this.props, 'projectData.getProject.projectName')}
          subheadline={
            process.env.REACT_APP_ENV === 'rigips'
              ? 'Mengenermittlung und Systemsuche - HORST'
              : 'ABK calcKit – der Baukasten für die schnelle Kalkulation'
          }
          error={errors ? true : false}
          message={
            errors ? 'Pflichtfelder ausfüllen!' : success ? 'Erfolgreich gespeichert.' : undefined
          }
          actions={
            <Button primary onClick={this.handleSave}>
              Speichern
            </Button>
          }
        />
        <SubNavigation
          items={[
            {
              label: 'Übersicht',
              link: `/projects/${id}`,
            },
            {
              link: `/projects/${id}/material-requirement`,
              label: 'Materialbedarf',
            },
            {
              link: `/projects/${id}/time-requirement`,
              label: 'Zeitbedarf',
            },
            {
              link: `/projects/${id}/settings`,
              label: 'Einstellungen',
            },
          ]}
        />
        <div className="px-48">
          <div className="mb-65">
            <h1 className="section-heading">Projekteinstellungen</h1>

            <div className="flex mb-45">
              <div className="w-2/12 flex items-end">
                <Input
                  name="projectNumber"
                  label="Projektnummer"
                  disabled
                  value={getProjectNumber(defaultTo(this.getValue('projectNumber'), ''))}
                  leftAlign
                  onChange={() => {}}
                />
              </div>
              <div
                className="flex items-end ml-30"
                style={{
                  width: 'calc(33.333333% + 30px)',
                }}
              >
                <Input
                  name="projectName"
                  label="BV-/Projektname"
                  required
                  value={defaultTo(this.getValue('projectName'), '')}
                  leftAlign
                  error={errors && errors.projectName ? errors.projectName : undefined}
                  onChange={(value) => this.setState({ projectName: value ? String(value) : '' })}
                />
              </div>
              <div
                className="flex items-end ml-30"
                style={{
                  width: 'calc(33.333333% + 30px)',
                }}
              >
                <Input
                  name="company"
                  label="Firma"
                  value={defaultTo(this.getValue('company'), '')}
                  leftAlign
                  error={errors && errors.company ? errors.company : undefined}
                  onChange={(value) => this.setState({ company: value ? String(value) : '' })}
                />
              </div>
              <div className="w-2/12 ml-30 flex items-end">
                <Input
                  name="date"
                  type="date"
                  value={isNil(this.getValue('date')) ? '' : new Date(this.getValue('date'))}
                  label="Datum"
                  leftAlign
                  error={errors && errors.date ? errors.date : undefined}
                  onDateChange={(value) => this.setState({ date: value ? value : null })}
                />
              </div>
            </div>

            <div className="flex mb-45">
              <div className="w-2/12 flex items-end">
                <Input
                  type="number"
                  name="hourlyRate"
                  label="Stundenlohn"
                  unit="EUR"
                  value={defaultTo(this.getValue('hourlyRate', true), 0)}
                  error={errors && errors.hourlyRate ? errors.hourlyRate : undefined}
                  onChange={(value) => this.setState({ hourlyRate: value ? Number(value) : null })}
                />
              </div>
              <div
                className="ml-30 flex items-end"
                style={{
                  width: 'calc(33.333333% + 30px)',
                }}
              >
                <Input
                  type="number"
                  name="tax"
                  value={defaultTo(this.getValue('tax', true), null)}
                  label="MwSt."
                  unit="%"
                  required
                  error={errors && errors.tax ? errors.tax : undefined}
                  onChange={(value) => this.setState({ tax: value ? Number(value) : null })}
                />
              </div>
              <div
                className="ml-30 flex items-end"
                style={{
                  width: 'calc(33.333333% + 30px)',
                }}
              ></div>
              <div
                className="ml-30 flex items-end"
                style={{
                  minWidth: '16.67%',
                }}
              ></div>
            </div>
          </div>

          <div className="mb-65">
            <h1 className="section-heading">Bauvorhaben</h1>
            <div className="flex mb-45">
              <div className="w-6/12 flex items-end">
                <Input
                  name="contact"
                  label="Ansprechpartner"
                  required
                  value={defaultTo(this.getValue('contact'), '')}
                  leftAlign
                  error={errors && errors.contact ? errors.contact : undefined}
                  onChange={(value) => this.setState({ contact: value ? String(value) : '' })}
                />
              </div>
              <div className="w-6/12 ml-30 flex items-end">
                <Input
                  name="street"
                  label="Straße"
                  value={defaultTo(this.getValue('street'), '')}
                  leftAlign
                  required
                  error={errors && errors.street ? errors.street : undefined}
                  onChange={(value) => this.setState({ street: value ? String(value) : '' })}
                />
              </div>
            </div>
            <div className="flex mb-45">
              <div className="w-4/12 flex items-end">
                <Input
                  name="zipCode"
                  type="number"
                  min={0}
                  label="PLZ"
                  required
                  value={defaultTo(this.getValue('zipCode'), '')}
                  leftAlign
                  error={errors && errors.zipCode ? errors.zipCode : undefined}
                  onChange={(value) => this.setState({ zipCode: value ? String(value) : '' })}
                />
              </div>
              <div className="w-4/12 ml-30 flex items-end">
                <Input
                  name="city"
                  label="Ort"
                  required
                  value={defaultTo(this.getValue('city'), '')}
                  leftAlign
                  error={errors && errors.city ? errors.city : undefined}
                  onChange={(value) => this.setState({ city: value ? String(value) : '' })}
                />
              </div>
              <div className="w-4/12 ml-30 flex items-end">
                <Select
                  label="Land"
                  options={countryOptions}
                  defaultValue="AT"
                  required
                  value={defaultTo(
                    countryOptions.find((option) => option.value === this.getValue('country')),
                    countryOptions[0],
                  )}
                  error={errors && errors.country ? errors.country : undefined}
                  onChange={(option) => this.setState({ country: String(option.value) })}
                />
              </div>
            </div>
            <div className="flex mb-45">
              <TextArea
                name="description"
                label="Beschreibung"
                value={defaultTo(this.getValue('description'), '')}
                error={errors && errors.description ? errors.description : undefined}
                onChange={(value) => this.setState({ description: value ? String(value) : '' })}
              />
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }
}

export default ProjectSettings
